/**
 * NZSC-3102 / NZSC-3103: 자산원클릭 B2B 다건 조회 (멀티 서치)
 *
 * 상태 머신 (multiSearchStep):
 *  - "entry"          : 1단계 조회방식 선택
 *  - "directInput"    : 직접 입력
 *  - "fileUpload"     : 엑셀 업로드 (기본/실패/30건초과 분기)
 *  - "fileParsing"    : 파일 파싱 중
 *  - "fileParsed"     : 파싱 성공
 *
 * BE 라우트는 NZSC-3103 진행 중 변동 가능. AcConstant 한 곳에서 갈아끼우면 됨.
 */
import SherlockAcConstant from "@/mixins/sherlock/const/sherlockAcConstant.js";
import { callPostApi, callPostFormDataApi } from "@/utils/apiWrapper";
import { getMultiSearchBatchStatus } from "@/utils/sherlock-owner-property";

const MAX_OWNERS = 30;
const MULTI_SEARCH_BATCH_STORAGE_KEY = "sherlock_multi_search_batch_tracking_v1";
const MULTI_SEARCH_BATCH_STORAGE_TTL_MS = 30 * 60 * 1000;

const blankOwner = () => ({ name: "", regno_front: "" });

const normalizeQuerySeqs = querySeqs => {
  if (!Array.isArray(querySeqs)) return [];
  return [
    ...new Set(
      querySeqs
        .filter(querySeq => querySeq != null && querySeq !== "")
        .map(Number)
        .filter(Number.isFinite),
    ),
  ];
};

const getBatchSessionStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch (_error) {
    return null;
  }
};

const clearPersistedMultiSearchBatch = () => {
  try {
    getBatchSessionStorage()?.removeItem(MULTI_SEARCH_BATCH_STORAGE_KEY);
  } catch (_error) {
    // 저장소가 막힌 환경에서도 조회 흐름은 계속한다.
  }
};

const persistMultiSearchBatch = state => {
  if (!state) return;
  const querySeqs = normalizeQuerySeqs(state._activeBatchQuerySeqs);
  const popupPending = state._multiSearchErrorPopupPending === true;

  if (querySeqs.length === 0 && !popupPending) {
    clearPersistedMultiSearchBatch();
    return;
  }

  const createdAt = Number(state._activeBatchCreatedAt);
  if (!Number.isFinite(createdAt) || createdAt <= 0) return;

  try {
    getBatchSessionStorage()?.setItem(
      MULTI_SEARCH_BATCH_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        querySeqs,
        popupPending,
        popupNotified: state._multiSearchErrorPopupNotified === true,
        createdAt,
      }),
    );
  } catch (_error) {
    // sessionStorage 용량/정책 오류가 사용자 조회 자체를 막지 않게 한다.
    clearPersistedMultiSearchBatch();
  }
};

const restorePersistedMultiSearchBatch = (now = Date.now()) => {
  const storage = getBatchSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(MULTI_SEARCH_BATCH_STORAGE_KEY);
    if (!raw) return null;

    const saved = JSON.parse(raw);
    const createdAt = Number(saved?.createdAt);
    const expired =
      !Number.isFinite(createdAt) ||
      createdAt <= 0 ||
      createdAt > now ||
      now - createdAt > MULTI_SEARCH_BATCH_STORAGE_TTL_MS;
    if (saved?.version !== 1 || expired) {
      clearPersistedMultiSearchBatch();
      return null;
    }

    const querySeqs = normalizeQuerySeqs(saved.querySeqs);
    const popupPending = saved.popupPending === true;
    if (querySeqs.length === 0 && !popupPending) {
      clearPersistedMultiSearchBatch();
      return null;
    }

    return {
      querySeqs,
      popupPending,
      popupNotified: saved.popupNotified === true || popupPending,
      createdAt,
    };
  } catch (_error) {
    clearPersistedMultiSearchBatch();
    return null;
  }
};

const isMultiSearchBatchExpired = (state, now = Date.now()) => {
  const createdAt = Number(state?._activeBatchCreatedAt);
  return (
    Number.isFinite(createdAt) &&
    createdAt > 0 &&
    (createdAt > now || now - createdAt > MULTI_SEARCH_BATCH_STORAGE_TTL_MS)
  );
};

export const state = {
  _multiSearchOpen: false,
  _multiSearchStep: "entry", // entry | directInput | fileUpload | fileParsing | fileParsed
  _multiSearchEntryChoice: null, // null | "direct" | "file" — 시안 2 선택 토글
  _multiSearchOwners: [], // [{name, regno_front}, ...]
  _multiSearchFileName: "",
  _multiSearchParseError: null, // null | "format" | "overflow" | "recognize_fail"
  _multiSearchParsedCount: 0,
  // 라우팅 이후 현재 다건 조회의 비동기 결과만 추적한다. 개인정보는 저장하지 않는다.
  _activeBatchQuerySeqs: [],
  _activeBatchCreatedAt: null,
  _multiSearchErrorPopupPending: false,
  _multiSearchErrorPopupNotified: false,
};

export const mutations = {
  SET_MULTI_SEARCH_OPEN(state, open) {
    state._multiSearchOpen = open;
  },
  SET_MULTI_SEARCH_STEP(state, step) {
    state._multiSearchStep = step;
  },
  SET_MULTI_SEARCH_OWNERS(state, owners) {
    state._multiSearchOwners = Array.isArray(owners) ? owners : [];
  },
  /**
   * 키스트로크당 전체 배열 spread 비용 제거 — idx 단위 in-place 갱신.
   * Vue2 반응성을 위해 Vue.set 패턴(splice) 사용.
   */
  UPDATE_MULTI_SEARCH_OWNER(state, { idx, patch }) {
    if (idx < 0 || idx >= state._multiSearchOwners.length) return;
    const next = { ...state._multiSearchOwners[idx], ...patch };
    state._multiSearchOwners.splice(idx, 1, next);
  },
  ADD_MULTI_SEARCH_OWNER(state) {
    if (state._multiSearchOwners.length >= MAX_OWNERS) return;
    state._multiSearchOwners.push(blankOwner());
  },
  REMOVE_MULTI_SEARCH_OWNER(state, idx) {
    if (state._multiSearchOwners.length <= 1) return;
    if (idx < 0 || idx >= state._multiSearchOwners.length) return;
    state._multiSearchOwners.splice(idx, 1);
  },
  SET_MULTI_SEARCH_ENTRY_CHOICE(state, choice) {
    state._multiSearchEntryChoice = choice === "direct" || choice === "file" ? choice : null;
  },
  SET_MULTI_SEARCH_FILE_NAME(state, name) {
    state._multiSearchFileName = name || "";
  },
  SET_MULTI_SEARCH_PARSE_ERROR(state, error) {
    state._multiSearchParseError = error || null;
  },
  SET_MULTI_SEARCH_PARSED_COUNT(state, count) {
    state._multiSearchParsedCount = Number(count) || 0;
  },
  SET_ACTIVE_MULTI_SEARCH_BATCH(
    state,
    { querySeqs, hasInitialFailure = false, createdAt = Date.now() },
  ) {
    state._activeBatchQuerySeqs = normalizeQuerySeqs(querySeqs);
    state._activeBatchCreatedAt = createdAt;
    state._multiSearchErrorPopupPending = hasInitialFailure === true;
    state._multiSearchErrorPopupNotified = hasInitialFailure === true;
  },
  RESTORE_ACTIVE_MULTI_SEARCH_BATCH(state, { querySeqs, popupPending, popupNotified, createdAt }) {
    state._activeBatchQuerySeqs = normalizeQuerySeqs(querySeqs);
    state._activeBatchCreatedAt = createdAt;
    state._multiSearchErrorPopupPending = popupPending === true;
    state._multiSearchErrorPopupNotified = popupNotified === true;
  },
  SET_MULTI_SEARCH_ERROR_POPUP_PENDING(state, pending) {
    state._multiSearchErrorPopupPending = pending === true;
  },
  SET_MULTI_SEARCH_ERROR_POPUP_NOTIFIED(state, notified) {
    state._multiSearchErrorPopupNotified = notified === true;
  },
  CLEAR_ACTIVE_MULTI_SEARCH_BATCH(state) {
    state._activeBatchQuerySeqs = [];
  },
  RESET_MULTI_SEARCH_BATCH_TRACKING(state) {
    state._activeBatchQuerySeqs = [];
    state._activeBatchCreatedAt = null;
    state._multiSearchErrorPopupPending = false;
    state._multiSearchErrorPopupNotified = false;
  },
  RESET_MULTI_SEARCH(state) {
    state._multiSearchOpen = false;
    state._multiSearchStep = "entry";
    state._multiSearchEntryChoice = null;
    state._multiSearchOwners = [];
    state._multiSearchFileName = "";
    state._multiSearchParseError = null;
    state._multiSearchParsedCount = 0;
  },
};

export const actions = {
  acOpenMultiSearch({ commit }) {
    commit("RESET_MULTI_SEARCH");
    commit("SET_MULTI_SEARCH_OPEN", true);
    commit("SET_MULTI_SEARCH_STEP", "entry");
    // NZSC-3102 운영 픽스: 모달 1단계 진입 시 "직접 입력하기" 기본 선택
    commit("SET_MULTI_SEARCH_ENTRY_CHOICE", "direct");
  },
  acCloseMultiSearch({ commit }) {
    commit("RESET_MULTI_SEARCH");
  },
  acTrackMultiSearchBatch({ state, commit }, { querySeqs, hasInitialFailure = false }) {
    // 이전 배치가 남은 상태에서 새 저장이 실패해도 과거 배치를 복원하지 않게 먼저 제거한다.
    clearPersistedMultiSearchBatch();
    commit("SET_ACTIVE_MULTI_SEARCH_BATCH", {
      querySeqs,
      hasInitialFailure,
      createdAt: Date.now(),
    });
    persistMultiSearchBatch(state);
  },
  acRestoreMultiSearchBatch({ state, commit }) {
    const hasCurrentBatchInMemory =
      state._activeBatchQuerySeqs.length > 0 ||
      state._activeBatchCreatedAt != null ||
      state._multiSearchErrorPopupPending === true;
    if (hasCurrentBatchInMemory) return false;

    const savedBatch = restorePersistedMultiSearchBatch();
    if (!savedBatch) return false;
    commit("RESTORE_ACTIVE_MULTI_SEARCH_BATCH", savedBatch);
    return true;
  },
  acUpdateMultiSearchBatchStatus({ state, commit }, items) {
    if (isMultiSearchBatchExpired(state)) {
      commit("RESET_MULTI_SEARCH_BATCH_TRACKING");
      clearPersistedMultiSearchBatch();
      return { hasFailure: false, isSettled: false };
    }

    const status = getMultiSearchBatchStatus(items, state._activeBatchQuerySeqs);
    if (status.hasFailure && !state._multiSearchErrorPopupNotified) {
      commit("SET_MULTI_SEARCH_ERROR_POPUP_NOTIFIED", true);
      commit("SET_MULTI_SEARCH_ERROR_POPUP_PENDING", true);
    }
    if (status.isSettled) {
      commit("CLEAR_ACTIVE_MULTI_SEARCH_BATCH");
      if (!state._multiSearchErrorPopupPending) {
        commit("RESET_MULTI_SEARCH_BATCH_TRACKING");
      }
    }
    persistMultiSearchBatch(state);
    return status;
  },
  acCloseMultiSearchErrorPopup({ state, commit }) {
    commit("SET_MULTI_SEARCH_ERROR_POPUP_PENDING", false);
    if (state._activeBatchQuerySeqs.length === 0) {
      commit("RESET_MULTI_SEARCH_BATCH_TRACKING");
    }
    persistMultiSearchBatch(state);
  },
  acGoMultiSearchStep({ commit }, step) {
    commit("SET_MULTI_SEARCH_STEP", step);
  },
  acSetMultiSearchEntryChoice({ commit }, choice) {
    commit("SET_MULTI_SEARCH_ENTRY_CHOICE", choice);
  },
  acStartMultiSearchDirectInput({ commit }) {
    // 직접 입력 진입 시 빈 행 2개로 시작 (피그마 dev 노트: 기본값 행 2개 노출)
    commit("SET_MULTI_SEARCH_OWNERS", [blankOwner(), blankOwner()]);
    commit("SET_MULTI_SEARCH_STEP", "directInput");
  },
  acStartMultiSearchFileUpload({ commit }) {
    commit("SET_MULTI_SEARCH_OWNERS", []);
    commit("SET_MULTI_SEARCH_FILE_NAME", "");
    commit("SET_MULTI_SEARCH_PARSE_ERROR", null);
    commit("SET_MULTI_SEARCH_PARSED_COUNT", 0);
    commit("SET_MULTI_SEARCH_STEP", "fileUpload");
  },

  /**************************************************************
   * 엑셀 파싱 — multipart/form-data
   * BE: POST /api/v1/sherlock/deal/parse-owner-properties-file
   * 응답: { owners: [{name, regno_front}], totalParsed }
   **************************************************************/
  async acParseMultiSearchFile({ commit }, file) {
    if (!file) return null;
    commit("SET_MULTI_SEARCH_FILE_NAME", file.name);
    commit("SET_MULTI_SEARCH_PARSE_ERROR", null);
    commit("SET_MULTI_SEARCH_STEP", "fileParsing");

    // 사용자 인지를 위한 최소 파싱 화면 노출 시간 (BE 응답이 빨라도 분석 중 UI 보이게)
    const parseStart = Date.now();

    const formData = new FormData();
    formData.append("file", file);

    const res = await callPostFormDataApi({
      url: SherlockAcConstant.POST_SHERLOCK_PARSE_OWNER_PROPERTIES_FILE,
      formData,
    });

    // 응답 받은 시점 — 건수 기반 최소 노출 시간 계산 후 부족하면 대기
    const ensureMinDelay = async (count = 0) => {
      const elapsed = Date.now() - parseStart;
      // 1.5초 + 건당 50ms (3건→1.65s, 10건→2.0s, 30건→3.0s)
      const minDelay = 1500 + Math.min(count, 30) * 50;
      if (elapsed < minDelay) {
        await new Promise(r => setTimeout(r, minDelay - elapsed));
      }
    };

    if (!res) {
      await ensureMinDelay(0);
      commit("SET_MULTI_SEARCH_PARSE_ERROR", "format");
      commit("SET_MULTI_SEARCH_STEP", "fileUpload");
      return null;
    }

    if (!res.ok) {
      const errCode = res.errorCode || res.message || "format";
      const isOverflow =
        errCode === "overflow" ||
        errCode === "OWNER_COUNT_OVERFLOW" ||
        errCode === "EXCEEDED_LIMIT";
      const isRecognizeFail =
        errCode === "recognize_fail" ||
        errCode === "PARSE_FAILED" ||
        errCode === "INVALID_FORMAT" ||
        errCode === "PARSE_FAIL" ||
        errCode === "EMPTY_FILE" ||
        errCode === "UNSUPPORTED_FORMAT";
      const errType = isOverflow ? "overflow" : isRecognizeFail ? "recognize_fail" : "format";
      const errCount = Number(res.data?.parsedCount) || Number(res.data?.totalParsed) || 0;
      await ensureMinDelay(errCount);
      commit("SET_MULTI_SEARCH_PARSE_ERROR", errType);
      commit("SET_MULTI_SEARCH_PARSED_COUNT", errCount);
      commit("SET_MULTI_SEARCH_STEP", "fileUpload");
      return null;
    }

    // BE 응답 envelope: { code, message, data: { result: { owners, totalParsed, warnings } } }
    // ask/process 처럼 result 한 단계 더 wrap 됨 — 둘 다 지원하도록 폴백.
    const data = res.data || {};
    const payload = data.result || data;
    const owners = Array.isArray(payload.owners) ? payload.owners : [];
    const totalParsed = Number(payload.totalParsed) || owners.length;

    // FE 측 안전망 — BE 가드와 별개로 30건 초과 차단.
    if (totalParsed > MAX_OWNERS || owners.length > MAX_OWNERS) {
      await ensureMinDelay(totalParsed);
      commit("SET_MULTI_SEARCH_PARSE_ERROR", "overflow");
      commit("SET_MULTI_SEARCH_PARSED_COUNT", totalParsed);
      commit("SET_MULTI_SEARCH_STEP", "fileUpload");
      return null;
    }

    // 0건 = 인식 실패와 동일하게 다룬다 (양식은 맞으나 데이터 row 가 없음).
    if (owners.length === 0) {
      await ensureMinDelay(0);
      commit("SET_MULTI_SEARCH_PARSE_ERROR", "recognize_fail");
      commit("SET_MULTI_SEARCH_PARSED_COUNT", 0);
      commit("SET_MULTI_SEARCH_STEP", "fileUpload");
      return null;
    }

    await ensureMinDelay(owners.length);
    commit("SET_MULTI_SEARCH_OWNERS", owners);
    commit("SET_MULTI_SEARCH_PARSED_COUNT", totalParsed);
    commit("SET_MULTI_SEARCH_STEP", "fileParsed");
    return { owners, totalParsed };
  },

  /**************************************************************
   * 다건 조회 ask — 포인트 사용 가능 여부 (단건 ask 패턴 따라감)
   * BE: POST /api/v1/sherlock/deal/ask/owner-properties-batch
   * BE DTO: owners[{owner_name, owner_regno_front}] — FE 내부는 {name, regno_front} 라 변환.
   **************************************************************/
  async acPostSherlockAskDealOwnerPropertiesBatch(_ctx, { owners }) {
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_ASK_DEAL_OWNER_PROPERTIES_BATCH,
      data: { owners: owners.map(o => ({ owner_name: o.name, owner_regno_front: o.regno_front })) },
    });
    return res;
  },

  /**************************************************************
   * 다건 조회 process — 일괄 조회 처리
   * BE: POST /api/v1/sherlock/deal/process/owner-properties-batch
   * 응답: { results: [{owner_name, owner_regno_front, deal_item_id, success}], successCount, failCount }
   **************************************************************/
  async acPostSherlockProcessOwnerPropertiesBatch(_ctx, { owners }) {
    const res = await callPostApi({
      url: SherlockAcConstant.POST_SHERLOCK_PROCESS_OWNER_PROPERTIES_BATCH,
      data: { owners: owners.map(o => ({ owner_name: o.name, owner_regno_front: o.regno_front })) },
    });
    return res;
  },
};

export const getters = {
  multiSearchValidOwners: state => {
    return state._multiSearchOwners.filter(
      o => o && o.name && o.name.trim() && /^\d{7}$/.test((o.regno_front || "").trim()),
    );
  },
  multiSearchCanSubmit: (state, getters) => {
    const valid = getters.multiSearchValidOwners;
    return valid.length > 0 && valid.length <= MAX_OWNERS;
  },
};

export const MULTI_SEARCH_MAX_OWNERS = MAX_OWNERS;
export const MULTI_SEARCH_BATCH_TRACKING_STORAGE_KEY = MULTI_SEARCH_BATCH_STORAGE_KEY;
export const MULTI_SEARCH_BATCH_TRACKING_TTL_MS = MULTI_SEARCH_BATCH_STORAGE_TTL_MS;

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
